import {
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  XIcon,
} from '@phosphor-icons/react';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import { Text } from '../../../components';
import { Style } from '../../../constants';

type FilterGroup = {
  id: string;
  name: string;
  labels: ReadonlyArray<{ id: string; name: string }>;
};

type Props = {
  groups: ReadonlyArray<FilterGroup>;
  initialFilters: Record<string, string[]>;
  onApply: (filters: Record<string, string[]>) => void;
  onClose: () => void;
};

export default function MobileFilterModal({
  groups,
  initialFilters,
  onApply,
  onClose,
}: Props) {
  const [activeGroupId, setActiveGroupId] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState<Record<string, string[]>>(() => {
    const copy: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(initialFilters)) {
      copy[k] = [...v];
    }
    return copy;
  });

  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const activeGroup = activeGroupId
    ? groups.find((g) => g.id === activeGroupId) ?? null
    : null;

  const toggleLabel = (groupId: string, labelId: string) => {
    setPending((prev) => {
      const current = prev[groupId] ?? [];
      const next = current.includes(labelId)
        ? current.filter((id) => id !== labelId)
        : [...current, labelId];
      return { ...prev, [groupId]: next };
    });
  };

  const getSelectedNames = (groupId: string): string | null => {
    const ids = pending[groupId];
    if (!ids || ids.length === 0) return null;
    const group = groups.find((g) => g.id === groupId);
    if (!group) return null;
    return ids
      .map((id) => group.labels.find((l) => l.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const handleApply = () => {
    const filtered: Record<string, string[]> = {};
    for (const [groupId, ids] of Object.entries(pending)) {
      if (ids.length > 0) {
        filtered[groupId] = ids;
      }
    }
    onApply(filtered);
    onClose();
  };

  // Option list view
  if (activeGroup) {
    const selectedIds = pending[activeGroup.id] ?? [];
    return (
      <html.div style={styles.modal}>
        <html.div style={styles.modalContent}>
          <html.div
            style={[
              Style.row,
              Style.alignCenter,
              Style.spaceBetween,
              styles.header,
            ]}
          >
            <html.button
              style={styles.headerButton}
              onClick={() => setActiveGroupId(null)}
            >
              <CaretLeftIcon size={20} />
            </html.button>
            <Text style={styles.headerTitle}>{activeGroup.name}</Text>
            <html.button style={styles.headerButton} onClick={onClose}>
              <XIcon size={20} />
            </html.button>
          </html.div>
          <html.div style={styles.list}>
            {activeGroup.labels.map((label) => {
              const isSelected = selectedIds.includes(label.id);
              return (
                <html.button
                  key={label.id}
                  style={[
                    Style.row,
                    Style.alignCenter,
                    Style.spaceBetween,
                    styles.listItem,
                  ]}
                  onClick={() => toggleLabel(activeGroup.id, label.id)}
                >
                  <Text style={styles.listItemLabel}>{label.name}</Text>
                  {isSelected && <CheckIcon size={20} color={color.accent} />}
                </html.button>
              );
            })}
          </html.div>
        </html.div>
        <html.button
          style={styles.applyButton}
          onClick={() => setActiveGroupId(null)}
        >
          Apply Filter
        </html.button>
      </html.div>
    );
  }

  // Category list view
  return (
    <html.div style={styles.modal}>
      <html.div style={styles.modalContent}>
        <html.div
          style={[
            Style.row,
            Style.alignCenter,
            Style.spaceBetween,
            styles.header,
          ]}
        >
          <html.div style={styles.headerButtonPlaceholder} />
          <Text style={styles.headerTitle}>Filter by...</Text>
          <html.button style={styles.headerButton} onClick={onClose}>
            <XIcon size={20} />
          </html.button>
        </html.div>
        <html.div style={styles.list}>
          {groups.map((group) => {
            const selectedNames = getSelectedNames(group.id);
            return (
              <html.button
                key={group.id}
                style={styles.categoryItem}
                onClick={() => setActiveGroupId(group.id)}
              >
                <html.div
                  style={[
                    Style.row,
                    Style.alignCenter,
                    Style.spaceBetween,
                    styles.categoryItemRow,
                  ]}
                >
                  <Text style={styles.listItemLabel}>{group.name}</Text>
                  <CaretRightIcon size={20} color={color.neutral60} />
                </html.div>
                {selectedNames && (
                  <Text style={styles.selectedValues}>{selectedNames}</Text>
                )}
              </html.button>
            );
          })}
        </html.div>
      </html.div>
      <html.button style={styles.applyButton} onClick={handleApply}>
        Apply Filters
      </html.button>
    </html.div>
  );
}

const styles = css.create({
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#fff',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingTop: 16,
    paddingBottom: 32,
    paddingInline: 16,
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  header: {},
  headerButton: {
    padding: 10,
    borderRadius: 8,
    borderWidth: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    color: color.neutral100,
  },
  headerButtonPlaceholder: {
    width: 40,
    height: 40,
  },
  headerTitle: {
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 1.4,
    color: color.neutral100,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  listItem: {
    width: '100%',
    paddingInline: 16,
    paddingBlock: 12,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  categoryItem: {
    width: '100%',
    paddingInline: 16,
    paddingBlock: 12,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    alignItems: 'flex-start',
  },
  categoryItemRow: {
    width: '100%',
  },
  listItemLabel: {
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 1.4,
    color: color.neutral100,
    textAlign: 'left',
  },
  selectedValues: {
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 1.4,
    color: color.neutral60,
  },
  applyButton: {
    width: '100%',
    height: 44,
    borderRadius: 100,
    borderWidth: 0,
    backgroundColor: color.accent,
    fontFamily: 'Inter, sans-serif',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 1.4,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
});
